import React, { useEffect, useRef, useState, useCallback } from 'react';
import { Network, AlertCircle, RefreshCw, Maximize2, Minimize2, ZoomIn, ZoomOut, RotateCcw } from 'lucide-react';

interface MermaidDiagramProps {
  diagram: string;
  title?: string;
  className?: string;
  defaultHeight?: number;
}

export const MermaidDiagram: React.FC<MermaidDiagramProps> = ({
  diagram,
  title = 'Analysis Flow',
  className = '',
  defaultHeight = 800
}) => {
  const containerRef = useRef<HTMLDivElement>(null);
  const [rendered, setRendered] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [isFullscreen, setIsFullscreen] = useState(false);
  const [mermaidLoaded, setMermaidLoaded] = useState(false);
  const [zoom, setZoom] = useState(1);

  // Zoom controls
  const handleZoomIn = useCallback(() => {
    setZoom(prev => Math.min(prev + 0.25, 3));
  }, []);

  const handleZoomOut = useCallback(() => {
    setZoom(prev => Math.max(prev - 0.25, 0.25));
  }, []);

  const handleResetZoom = useCallback(() => {
    setZoom(1);
  }, []);

  // Load mermaid library dynamically
  useEffect(() => {
    const loadMermaid = async () => {
      try {
        // Check if mermaid is already loaded
        if (window.mermaid) {
          setMermaidLoaded(true);
          return;
        }

        // Dynamically import mermaid
        const mermaid = await import('mermaid');
        mermaid.default.initialize({
          startOnLoad: false,
          theme: 'base',
          securityLevel: 'loose',
          flowchart: {
            useMaxWidth: true,
            htmlLabels: true,
            curve: 'basis',
            padding: 20,
            nodeSpacing: 50,
            rankSpacing: 70,
            diagramPadding: 20
          },
          themeVariables: {
            // Modern, vibrant color scheme
            primaryColor: '#818cf8',      // Indigo for main nodes
            primaryTextColor: '#1e1b4b',  // Dark indigo text
            primaryBorderColor: '#6366f1',
            secondaryColor: '#fcd34d',    // Amber for secondary
            secondaryTextColor: '#78350f',
            secondaryBorderColor: '#f59e0b',
            tertiaryColor: '#6ee7b7',     // Emerald for tertiary
            tertiaryTextColor: '#064e3b',
            tertiaryBorderColor: '#10b981',
            // Line and background
            lineColor: '#6366f1',
            textColor: '#374151',
            mainBkg: '#f8fafc',
            // Node styling
            nodeBorder: '#6366f1',
            clusterBkg: '#f1f5f9',
            clusterBorder: '#cbd5e1',
            // Edge styling
            edgeLabelBackground: '#ffffff',
            // Font
            fontFamily: 'Inter, system-ui, sans-serif',
            fontSize: '14px'
          }
        });
        window.mermaid = mermaid.default;
        setMermaidLoaded(true);
      } catch (err) {
        console.error('Failed to load mermaid:', err);
        setError('Failed to load diagram library');
      }
    };

    loadMermaid();
  }, []);

  // Render diagram when mermaid is loaded
  useEffect(() => {
    const renderDiagram = async () => {
      if (!mermaidLoaded || !containerRef.current || !diagram) {
        return;
      }

      try {
        setError(null);
        containerRef.current.innerHTML = '';

        // Create a unique ID for this diagram
        const id = `mermaid-${Math.random().toString(36).substr(2, 9)}`;

        // Render the diagram
        const { svg } = await window.mermaid.render(id, diagram);
        containerRef.current.innerHTML = svg;

        // Adjust SVG to fit content naturally
        const svgElement = containerRef.current.querySelector('svg');
        if (svgElement) {
          // Remove fixed dimensions and let it scale naturally
          svgElement.style.maxWidth = '100%';
          svgElement.style.height = 'auto';
          // Preserve aspect ratio
          svgElement.setAttribute('preserveAspectRatio', 'xMidYMid meet');
        }

        setRendered(true);
      } catch (err) {
        console.error('Failed to render mermaid diagram:', err);
        setError('Failed to render diagram. Please check the syntax.');
        setRendered(false);
      }
    };

    renderDiagram();
  }, [diagram, mermaidLoaded]);

  const handleRetry = () => {
    setError(null);
    setRendered(false);
    // Force re-render by updating state
    setMermaidLoaded(false);
    setTimeout(() => setMermaidLoaded(true), 100);
  };

  if (!diagram) {
    return null;
  }

  return (
    <div className={`space-y-3 ${className}`}>
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <Network className="w-5 h-5 text-primary-600" />
          <h3 className="text-lg font-semibold text-neutral-900">{title}</h3>
        </div>
        <div className="flex items-center gap-2">
          {/* Zoom Controls */}
          <div className="flex items-center gap-1 border border-neutral-200 rounded-lg px-1 py-0.5 bg-white">
            <button
              onClick={handleZoomOut}
              className="p-1.5 text-neutral-500 hover:text-neutral-700 hover:bg-neutral-100 rounded transition-colors"
              title="Zoom out"
              disabled={zoom <= 0.25}
            >
              <ZoomOut className="w-4 h-4" />
            </button>
            <span className="text-xs font-medium text-neutral-600 min-w-[3rem] text-center">
              {Math.round(zoom * 100)}%
            </span>
            <button
              onClick={handleZoomIn}
              className="p-1.5 text-neutral-500 hover:text-neutral-700 hover:bg-neutral-100 rounded transition-colors"
              title="Zoom in"
              disabled={zoom >= 3}
            >
              <ZoomIn className="w-4 h-4" />
            </button>
            <button
              onClick={handleResetZoom}
              className="p-1.5 text-neutral-500 hover:text-neutral-700 hover:bg-neutral-100 rounded transition-colors"
              title="Reset zoom"
            >
              <RotateCcw className="w-4 h-4" />
            </button>
          </div>
          {/* Fullscreen */}
          <button
            onClick={() => setIsFullscreen(!isFullscreen)}
            className="p-1.5 text-neutral-500 hover:text-neutral-700 hover:bg-neutral-100 rounded transition-colors"
            title={isFullscreen ? 'Exit fullscreen' : 'Fullscreen'}
          >
            {isFullscreen ? (
              <Minimize2 className="w-4 h-4" />
            ) : (
              <Maximize2 className="w-4 h-4" />
            )}
          </button>
        </div>
      </div>

      <div
        className={`
          border border-neutral-200 rounded-lg bg-white overflow-hidden
          ${isFullscreen ? 'fixed inset-4 z-50 shadow-2xl' : ''}
        `}
      >
        {isFullscreen && (
          <div className="flex items-center justify-between p-3 border-b border-neutral-200 bg-neutral-50">
            <span className="font-medium text-neutral-900">{title}</span>
            <button
              onClick={() => setIsFullscreen(false)}
              className="p-1.5 text-neutral-500 hover:text-neutral-700 hover:bg-neutral-200 rounded transition-colors"
            >
              <Minimize2 className="w-4 h-4" />
            </button>
          </div>
        )}

        {/* Loading State */}
        {!rendered && !error && (
          <div className="flex items-center justify-center p-8 text-neutral-500">
            <RefreshCw className="w-5 h-5 animate-spin mr-2" />
            <span className="text-sm">Rendering diagram...</span>
          </div>
        )}

        {/* Error State */}
        {error && (
          <div className="flex flex-col items-center justify-center p-8 text-red-600 bg-red-50">
            <AlertCircle className="w-8 h-8 mb-2" />
            <p className="text-sm font-medium">{error}</p>
            <button
              onClick={handleRetry}
              className="mt-3 px-3 py-1.5 text-sm bg-red-100 hover:bg-red-200 rounded transition-colors"
            >
              Retry
            </button>
            {/* Show raw diagram code for debugging */}
            <details className="mt-4 w-full max-w-lg">
              <summary className="text-xs text-red-500 cursor-pointer">Show diagram code</summary>
              <pre className="mt-2 p-2 text-xs bg-red-100 rounded overflow-auto max-h-40">
                {diagram}
              </pre>
            </details>
          </div>
        )}

        {/* Diagram Container - scrollable with zoom */}
        <div
          className={`
            overflow-auto
            ${isFullscreen ? 'h-[calc(100%-56px)]' : ''}
            ${!rendered && !error ? 'hidden' : ''}
          `}
          style={{
            display: rendered ? 'block' : 'none',
            maxHeight: isFullscreen ? undefined : `${defaultHeight}px`
          }}
        >
          <div
            ref={containerRef}
            className="p-4 min-w-fit"
            style={{
              transform: `scale(${zoom})`,
              transformOrigin: 'top left',
              transition: 'transform 0.2s ease-out'
            }}
          />
        </div>
      </div>

      <p className="text-xs text-neutral-500 italic">
        This visualization shows the recommended analysis flow for answering this strategic question.
      </p>
    </div>
  );
};

// Type declaration for window.mermaid
declare global {
  interface Window {
    mermaid: {
      initialize: (config: Record<string, unknown>) => void;
      render: (id: string, definition: string) => Promise<{ svg: string }>;
    };
  }
}

export default MermaidDiagram;
